/*

	Lilact
	Copyright (C) 2024-2026 Arash Kazemi <contact.arash.kazemi@gmail.com>
	All rights reserved.

	BSD-2-Clause

	Redistribution and use in source and binary forms, with or without
	modification, are permitted provided that the following conditions are met:

	* Redistributions of source code must retain the above copyright
		notice, this list of conditions and the following disclaimer.
	* Redistributions in binary form must reproduce the above copyright
		notice, this list of conditions and the following disclaimer in the
		documentation and/or other materials provided with the distribution.

	THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
	AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
	IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
	ARE DISCLAIMED. IN NO EVENT SHALL <COPYRIGHT HOLDER> BE LIABLE FOR ANY
	DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
	(INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
	LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
	ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
	(INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
	THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.

*/

import Lilact from "./lilact.jsx";
import { isEmpty } from "./misc.jsx";
import { LAZY } from "./symbols.jsx";
import { injectGlobal } from "@emotion/css";

function joinPaths(basePath, relativePath) {
	const absolute = relativePath.startsWith("/");
	const parts = (absolute ? "" : basePath)
		.split("/")
		.filter(Boolean);

	if (!absolute && !basePath.endsWith("/")) {
		parts.pop();
	}

	for (const part of relativePath.split("/")) {
		if (!part || part === ".") continue;

		if (part === "..") {
			parts.pop();
		} else {
			parts.push(part);
		}
	}

	return `${absolute ? "/" : ""}${parts.join("/")}`;
}

function asError(value, fallback = "Unknown error") {
	if (value instanceof Error) return value;
	if (value?.error instanceof Error) return value.error;

	const error = new Error(
		value?.message == null ? fallback : String(value.message)
	);

	if (value && typeof value === "object") {
		if (value.name) error.name = value.name;
		if (value.stack) error.stack = value.stack;

		for (const key of Object.keys(value)) {
			if (!(key in error)) {
				error[key] = value[key];
			}
		}
	}

	return error;
}

/*
* The first module that catches an error owns its source location.
* Parent modules must never replace it.
*/
function markSource(value, path) {
	const error = asError(value);

	if (!error.lilact_source) {
		error.lilact_source = { path };
	}

	return error;
}

function report(value, path) {
	const error = markSource(value, path);

	if (error.isTraced) {
		return error;
	}

	if (typeof Lilact.traceError === "function") {
		return Lilact.traceError(error, path);
	}

	Lilact.error = error;
	return error;
}

/** @ignore */
export const required_scripts = {};

function createModule(path, {
	code = ""
} = {}) {
	const module = {
		path,
		code: String(code),
		mappings: [],
		meta: {},
		exports: {},
		loaded: false,
		request: undefined,
		error: undefined,
	};

	required_scripts[path] = module;
	return module;
}

function loadModule(path, options = {}) {
	if (Lilact.importObjectPaths?.[path]) {
		return Lilact.importObjectPaths[path];
	}

	if (options.requirer?.path) {
		path = joinPaths(options.requirer.path, path);
	}

	const loadAsync = Boolean(Lilact[LAZY]) ||
										Boolean(options.isLazy);

	const module = getOrCreateModule(path, {});

	if (module.loaded && !options.forceReload) {
		return module.exports;
	}

	if (path.startsWith("#")) {
		const element = document.getElementById(path.slice(1));

		if (!element) {
			throw report(
				new Error(`Required element not found (${path})`),
				path
			);
		}

		return run(
			element.textContent || "",
			path,
			{forceReload: options.forceReload}
		);
	}

	if (loadAsync) {
		Lilact[LAZY] = false;

		if (module.request && !options.forceReload) {
			return module.request;
		}

		return loadAsyncResource(path, module, options);
	}

	const resolved = Lilact.resolver?.(path);

	if (resolved != null) {
		if (path.endsWith(".css")) {
			injectGlobal(String(resolved));
			module.code = String(resolved);
			module.loaded = true;
			return undefined;
		}

		return run(
			String(resolved),
			path,
			{forceReload: options.forceReload}
		);
	}

	const request = new XMLHttpRequest();

	try {
		request.open("GET", path, false);
		request.send(null);
	} catch (value) {
		throw report(value, path);
	}

	if (request.status >= 200 && request.status < 300) {
		if (path.endsWith(".css")) {
			module.code = request.responseText;
			injectGlobal(module.code);
			module.loaded = true;
			return undefined;
		}

		return run(
			request.responseText,
			path,
			{forceReload: true}
		);
	}

	throw report(
		new Error(
		`Unable to load ${path}: HTTP ${request.status || 0}`
	),
		path
	);
}


/**
 * Loads a module using Lilact's module loader.
 *
 * This function provides the runtime `require()` implementation used by
 * transpiled Lilact modules.
 *
 * @param {string} path - Module path or URL to load.
 * @param {object} [options] - Module-loading options.
 * @param {{path: string}} [options.requirer] - Module that requested this
 * resource.
 * @param {boolean} [options.isLazy=false] - Forces asynchronous loading.
 * @param {boolean} [options.forceReload=false] - Reloads the module even if
 * it has already been evaluated.
 * @returns {*} The module exports, or a Promise for asynchronous modules.
 * @throws {Error} If the module cannot be loaded or evaluated.
 */
export function require(path) 
{
	let options = {};

	if (
		arguments.length === 2 &&
		arguments[1] &&
		typeof arguments[1] === "object"
	) {
		options = arguments[1];
	}

	return loadModule(path, options);
}


function makeImportsObject(mod, options = {}) {
	mod.importsObject ??= {};

	for (const key of Object.keys(mod.importsObject)) {
		delete mod.importsObject[key];
	}

	for (const path in mod.meta.imports) {
		const imps = mod.meta.imports[path];

		const exps = loadModule(path, {
			requirer: mod,
			forceReload: options.forceReload,
		});

		for (const i in imps.named_imports) {
			if (i === "") continue;

			const exportedName = imps.named_imports[i];

			if (!Object.prototype.hasOwnProperty.call(exps, exportedName)) {
				throw new Error(
				`Imported module does not export any "${i}".`
			);
			}

			mod.importsObject[i] = exps[exportedName];
		}

		for (const i of imps.import_defaults) {
			if (!Object.prototype.hasOwnProperty.call(exps, "default")) {
				throw new Error(
					"Imported module does not export a default."
				);
			}

			mod.importsObject[i] = exps.default;
		}

		for (const i of imps.import_stars) {
			mod.importsObject[i] = { ...exps };
		}
	}

	return mod.importsObject;
}


function exportEntries(value) {
	return value && typeof value === "object"
			? value
			: { default: value };
}

function replaceReloadedExports(previousExports, nextExports) {
	if (!previousExports) return;

	const oldExports = exportEntries(previousExports);
	const newExports = exportEntries(nextExports);

	for (const name of Object.keys(oldExports)) {
		const oldExport = oldExports[name];
		const newExport = newExports[name];

		if (
			typeof oldExport === "function" &&
			typeof newExport === "function"
		) {
			Lilact.replaceAllComponentInstances(oldExport, newExport);
		}
	}
}


/**
 * Transpiles and evaluates JSX source code as a Lilact module.
 *
 * The source is registered under the supplied path, transpiled with source
 * mappings and runtime tracing enabled, evaluated in the global Lilact
 * runtime, and returned as module exports. Reloaded component exports are
 * compared with their previous versions to support hot loading.
 *
 * @param {string} jsx - JSX or JavaScript source code to evaluate.
 * @param {string} [path=`InlineJSX-${Lilact.eval_num + 1}`] - Module path
 * used for caching, diagnostics, and source mapping.
 * @param {object} [options] - Evaluation options.
 * @param {boolean} [options.forceReload=false] - Forces dependent resources
 * to reload when they are required.
 * @returns {*} The evaluated module exports.
 * @throws {Error} If transpilation or runtime evaluation fails.
 */

export function run(
	jsx,
	path = `InlineJSX-${++Lilact.eval_num}`,
	options = {}
) {
	let module = required_scripts[path];

	const previousExports =
	module?.loaded
	? module.exports
	: undefined;

	if (!module) {
		module = createModule(path, {
			code: jsx,
		});
	} else {
		module.path = path;
		module.code = String(jsx);
		module.mappings = [];
		module.exports = {};
		module.loaded = false;
		module.error = undefined;
		module.meta = {};
	}

	let processed;

	try {
		processed = Lilact.transpileJSX(String(jsx), {
			path,
			mappings: module.mappings,
			meta: module.meta,
			factory: "createComponent",
			appendSourcemap: true,
			injectTraceLabels: true,
			produceCJS: true,
			addLocationProp: true,
			blocksInfo: Lilact.blocksInfo,
		});
	} catch (value) {
		const error = asError(value);

		error.fileName ??= path;
		error.lilact_source ??= { path };
		error.sourcePhase = "transpile";

		module.error = error;
		Lilact.error = error;

		throw error;
	}

	// console.log(processed)
	if (typeof Lilact.scanBlockLabels === "function") {
		Lilact.scanBlockLabels(processed, path);
	}

	try {
		globalThis.Lilact = Lilact;
		globalThis.createComponent = Lilact.createComponent;
		globalThis.Fragment = Lilact.Fragment;

		const result = eval(processed);

		module.loaded = true;

		const nextExports = isEmpty(module.exports)
		? result
		: module.exports;

		replaceReloadedExports(previousExports, nextExports);

		return nextExports;
	} catch (value) {
		const error = report(value, path);

		error.sourcePhase ??= "runtime";
		module.error = error;

		throw error;
	}
}

function getOrCreateModule(path, options = {}) {
	return required_scripts[path] || createModule(path, options);
}

function loadAsyncResource(path, module, options = {}) 
{
	let source;

	try {
		const resolved = Lilact.resolver?.(path);

		if (resolved == null) {
			if (module.code && !module.loaded) {
				source = Promise.resolve(module.code);
			} else {
				source = fetch(path).then(response => {
					if (!response.ok) {
						throw report(
							new Error(
								`Unable to load ${path}: HTTP ${response.status}`
							),
							path
						);
					}

					return response.text();
				});
			}
		} else {
			source = Promise.resolve(resolved);
		}
	} catch (value) {
		source = Promise.reject(report(value, path));
	}

	/*
	* Always convert the source to a promise. The promise is assigned to the
	* module before any caller can make another require() call.
	*/
	let request;

	request = Promise.resolve(source)
		.then(sourceText => {
			module.code = String(sourceText);

			if (path.endsWith(".css")) {
				injectGlobal(module.code);
				module.loaded = true;
				return undefined;
			}

			return run(module.code, path, options);
		})
		.then(result => result?.default ?? result)
		.catch(error => {
			module.error = report(error, path);
			throw module.error;
		})
		.finally(() => {
			/*
			* Do not remove a newer request accidentally if a future forceReload
			* request replaced this one.
			*/
			if (module.request === request) {
				module.request = undefined;
			}
		});

	module.request = request;
	return request;
}

/**
 * Creates a lazily evaluated component from a module or promise factory.
 *
 * The factory is executed while lazy-loading mode is enabled. The returned
 * component suspends by throwing the pending promise until the factory
 * resolves, or throws the loading error if the factory fails.
 *
 * @param {Function} factory - Function that returns a component, module,
 * promise, or thenable resolving to a component or module.
 * @returns {Function} A component that renders the resolved component.
 * @throws {*} The pending promise or loading error while the component is
 * unresolved.
 */
export function lazy(factory) 
{
	let status = "pending";
	let result;

	Lilact[LAZY] = true;

	try {
		result = factory();
	} catch (error) {
		status = "error";
		result = error;
	} finally {
		Lilact[LAZY] = false;
	}

	if (Lilact.isThenable(result)) {
		result.then(
			value => {
				status = "success";
				result = value;
			},
			error => {
				status = "error";
				result = error;
			}
		);
	} else if (status !== "error") {
		status = "success";
	}

	function LazyComponent(props) {
		if (status === "pending" || status === "error") {
			throw result;
		}

		const Component = result?.default ?? result;
		return <Component {...props} />;
	}

	return LazyComponent;
}

function scriptTags() 
{
	return Array.from(
		document.querySelectorAll('script[type="text/jsx"]')
	).map(element => ({
		src: element.getAttribute("src"),
		content: element.textContent || "",
	}));
}

/**
 * Loads and evaluates every JSX script in the current document.
 *
 * External scripts are loaded through {@link require}. Inline scripts are
 * evaluated directly through {@link run}. Scripts are processed in document
 * order.
 *
 * @returns {Promise<void>} A promise that resolves after all scripts finish.
 * @throws {Error} If an external or inline script cannot be loaded or
 * evaluated.
 */

export async function runScripts() {
  for (const script of scriptTags()) {
    if (script.src) {
      await require(script.src);
    } else if (script.content) {
      run(script.content);
    }
  }
}